import { CatalogProvider } from '@common/catalog/CatalogProvider';
import { IDELogContextProvider } from '@common/Footer/context';
import { CodeGenerationContextProvider } from '@common/git/CodeGeneration/context';
import { IdeActionProvider, IDEParamsProvider, IdeStateProvider, useIDE } from '@common/ide/state';
import { useIDEParamsForIDEPage } from '@common/ide/useIDEParamsForIDEPage';
import { ReactDndManager } from '@common/react-dnd-manager';
import { Entity, IDEEntity } from '@common/types/Entity';
import { BackEndCodeLanguageType, WorkflowModeType } from '@prophecy/graphql-types/enums';
import { ReactNode, useMemo } from 'react';
import { DndProvider } from 'react-dnd';

import { ExecutionServiceProvider } from '../../../context/execution';
import { useExecutionServiceInstance } from '../../../data/executionHooks';

export function MockIDE({
  uid,
  entity,
  branch,
  projectId,
  projectName,
  language,
  repositoryPath,
  path,
  children
}: {
  entity: IDEEntity;
  uid: string;
  projectId: string;
  isUIDFromAnotherProject?: boolean;
  projectName: string;
  language: BackEndCodeLanguageType;
  repositoryPath?: string;
  path?: string;
  branch?: string;
  workflowMode?: WorkflowModeType;
  children: ReactNode;
}) {
  const { state, actions, setActions } = useIDE({
    uid,
    entity,
    projectId,
    projectName,
    language,
    branch,
    repositoryPath,
    path
  });

  const ideKey = `${state.ide.repositoryPath} ${state.ide.uid}`;
  const executionService = useExecutionServiceInstance(ideKey);

  const codeGenProps = useMemo(
    () => ({
      isPipelineIDE: entity === Entity.Pipeline || entity === Entity.Configuration
    }),
    [entity]
  );

  const ideParamsValue = {
    ...useIDEParamsForIDEPage(),
    entity: Entity.Pipeline.toLowerCase()
  };

  return (
    <ExecutionServiceProvider value={executionService}>
      <CatalogProvider>
        <DndProvider manager={ReactDndManager}>
          <IdeStateProvider value={state}>
            <IdeActionProvider value={{ setActions, actions }}>
              <IDEParamsProvider params={ideParamsValue}>
                <IDELogContextProvider entity={entity}>
                  <CodeGenerationContextProvider {...codeGenProps}>{children}</CodeGenerationContextProvider>
                </IDELogContextProvider>
              </IDEParamsProvider>
            </IdeActionProvider>
          </IdeStateProvider>
        </DndProvider>
      </CatalogProvider>
    </ExecutionServiceProvider>
  );
}
